import apis from '../../utils/apis';
import { isMarketplaceEnabled } from '../../utils/config';

/**
 * The caller's account type and minimal profile.
 *
 * Contract: pons-backend-platform `domains/accounts/README.md`. The rules for
 * each type (what it may reach) live in the backend's
 * `core/auth/account_types.py`; the frontend only reads `shell` to decide
 * which app to render.
 */

/** Which app an account gets. Unknown values render nothing but sign-out. */
export type AccountShell = 'workspace' | 'client';

export interface AccountTypeInfo {
  key: string;
  shell: AccountShell | string;
  needs_organization: boolean;
  label?: string;
}

export interface Account {
  /** null until the user has chosen (or the sign-up link chose for them). */
  account_type: AccountTypeInfo | null;
  can_choose: boolean;
  choices: AccountTypeInfo[];
  email_verified: boolean;
  profile: { first_name: string; last_name: string; country: string | null };
  needs_profile: boolean;
}

const unwrap = <T>(res: { data: { data: T } }): T => res.data.data;

export const getAccount = async (): Promise<Account> => unwrap<Account>(await apis.get('/account/me'));

export const chooseAccountType = async (accountType: string): Promise<Account> =>
  unwrap<Account>(await apis.post('/account/type', { account_type: accountType }));

export const updateAccountProfile = async (body: {
  first_name: string;
  last_name: string;
  country: string;
}): Promise<Account> => unwrap<Account>(await apis.put('/account/profile', body));

export interface ResendResult {
  status: 'sent' | 'already_verified';
  /** Seconds before another resend is allowed. */
  retry_after?: number;
}

/** Ask Auth0 to send "confirm your email" again. Throttled server-side (429). */
export const resendVerificationEmail = async (): Promise<ResendResult> =>
  unwrap<ResendResult>(await apis.post('/account/verification-email'));

/*
  SIGN-UP INTENT survives the trip through Auth0.

  `/get-help` stores it before sending the visitor to Auth0's sign-up, and the
  app reads it back once the user is signed in. localStorage, not
  sessionStorage: confirming the email opens a new tab, and the user may sign
  in from there. It expires after a week so an old visit can't decide a later
  account.
*/
const INTENT_KEY = 'pons:signup-intent';
const INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const rememberSignupIntent = (accountType: string): void => {
  try {
    localStorage.setItem(INTENT_KEY, JSON.stringify({ accountType, at: Date.now() }));
  } catch {
    // Storage blocked: the user gets the "Who are you?" step instead.
  }
};

/** Read and forget the stored intent. */
export const takeSignupIntent = (): string | null => {
  try {
    const raw = localStorage.getItem(INTENT_KEY);
    localStorage.removeItem(INTENT_KEY);
    if (!raw) return null;
    const { accountType, at } = JSON.parse(raw);
    return typeof accountType === 'string' && Date.now() - Number(at) < INTENT_TTL_MS ? accountType : null;
  } catch {
    return null;
  }
};

/** The path a "Find a lawyer" link on pons.io points at. */
export const CLIENT_SIGNUP_PATH = '/get-help';

/**
 * The account's type decides which app renders (workspace or client).
 *
 * A user who arrived through `/get-help` has a stored sign-up intent; it is
 * applied here, once, while the type is still unchosen. Anyone else with an
 * unchosen type gets the "Who are you?" step in AppShell.
 *
 * Returns null when the lookup fails (e.g. an older backend): AppShell then
 * behaves exactly as it did before account types existed.
 */
export const loadAccount = async (): Promise<Account | null> => {
  try {
    const account = await getAccount();
    const arrivedAsClient = isMarketplaceEnabled() && window.location.pathname === CLIENT_SIGNUP_PATH;
    const intent = takeSignupIntent() || (arrivedAsClient ? 'client' : null);
    if (!account.account_type && account.can_choose && intent
        && account.choices.some((c) => c.key === intent)) {
      return await chooseAccountType(intent);
    }
    return account;
  } catch {
    return null;
  }
};
